import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState } from 'react';
import {
	StyledDescription,
	StyledTextWrapper,
	StyledTitle,
	StyledVideo,
	StyledVideoPlayer,
} from './styled-video-player';

const displayName = 'VideoPlayer';

const defaultProps = {
	className: 'fuse-video-player',
	dataAnalyticsId: undefined,
	dataTestId: 'fuse-video-player-test-id',
	description: undefined,
	onPauseCallback: undefined,
	onPlayCallback: undefined,
	poster: '',
	preload: 'none',
	ratio: '16:9',
	title: undefined,
	titleTag: 'h2',
	tracks: undefined,
	videoOptions: undefined,
};

const propTypes = {
	className: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	dataTestId: PropTypes.string,
	description: PropTypes.string,
	onPauseCallback: PropTypes.func,
	onPlayCallback: PropTypes.func,
	poster: PropTypes.string,
	preload: PropTypes.oneOf(['none', 'auto', 'metadata']),
	ratio: PropTypes.oneOf(['16:9', '1:1', '4:3', '5:4', '3:4']),
	sources: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			type: PropTypes.string.isRequired,
		}),
	).isRequired,
	title: PropTypes.string,
	titleTag: PropTypes.string,
	tracks: PropTypes.arrayOf(
		PropTypes.shape({
			src: PropTypes.string.isRequired,
			type: PropTypes.oneOf(['captions', 'subtitles']).isRequired,
			label: PropTypes.string.isRequired,
		}),
	),
	videoOptions: PropTypes.shape({}),
};

function VideoPlayer({
	className,
	dataAnalyticsId,
	dataTestId,
	description,
	onPauseCallback,
	onPlayCallback,
	poster,
	preload,
	ratio,
	sources,
	title,
	titleTag,
	tracks,
	videoOptions,
	...props
}) {
	const [initialClick, setInitialClick] = useState(false);

	const videoRef = useRef();

	const onPauseHandler = useCallback(
		(e, ref) => {
			if (typeof onPauseCallback === 'function') onPauseCallback(e, ref);
		},
		[onPauseCallback],
	);

	const onPlayHandler = useCallback(
		(e, ref) => {
			if (typeof onPlayCallback === 'function') onPlayCallback(e, ref);
		},
		[onPlayCallback],
	);

	const onClickHandler = () => {
		videoRef.current.play();
		setInitialClick(true);
	};

	const hasText = title || description;

	return (
		<StyledVideoPlayer className={className} data-testid={dataTestId} {...props}>
			{hasText && (
				<StyledTextWrapper>
					{title && (
						<StyledTitle size={{ xxs: '2', m: '3' }} tag={titleTag} type="heading">
							{title}
						</StyledTitle>
					)}

					{description && (
						<StyledDescription size="1" type="paragraph">
							{description}
						</StyledDescription>
					)}
				</StyledTextWrapper>
			)}
			<StyledVideo
				ref={videoRef}
				data-analyticsid={dataAnalyticsId}
				controls
				crossOrigin="anonymous"
				poster={poster}
				preload={preload}
				ratio={ratio}
				onClick={() => !initialClick && onClickHandler()}
				onPause={(e) => onPauseHandler(e, videoRef)}
				onPlay={(e) => onPlayHandler(e, videoRef)}
				{...videoOptions}
			>
				{sources.map(({ src, type, ...source }) => (
					<source
						key={src}
						className="fuse-video-player__source"
						src={src}
						type={`video/${type}`}
						{...source}
					/>
				))}
				{tracks &&
					tracks.map(({ src, type, label, ...track }) => (
						<track
							key={src}
							className="fuse-video-player__track"
							src={src}
							kind={type}
							label={label}
							{...track}
						/>
					))}
				Your browser does not support the video tag.
			</StyledVideo>
		</StyledVideoPlayer>
	);
}

VideoPlayer.displayName = displayName;
VideoPlayer.propTypes = propTypes;
VideoPlayer.defaultProps = defaultProps;

export { VideoPlayer };
export default VideoPlayer;
